import React, { useContext, useEffect, useState } from 'react';
import { Paper, Tab, Tabs } from '@material-ui/core';

import { catalogApi } from 'api/catalog';
import { fbmApi as api } from 'api/fbm';
import { isBlueApronOrMarleySpoonAccount } from 'components/fbm/components/partnerCatalog/partnerCatalogUtils';
import { ViewPartnerCatalog } from 'components/fbm/components/partnerCatalog/ViewPartnerCatalog';
import { UserContext } from 'contexts/UserContext';
import { FBMAccountPicker, Notifications, TabPanel } from 'shared';

import CreateFBMItem from './CreateFBMItem';
import CreateFBMPackaging from './CreateFBMPackaging';
import { ViewFBMItems } from './viewFBMItems';
import { ViewFBMPackaging } from './viewFBMPackaging';

const CATALOG_TAB = '4';

function FBMCatalog() {
    const [packagingTypesOpts, setPackagingTypesOpts] = useState([]);
    const [selectedTab, setSelectedTab] = useState('0');
    const [tempZones, setTempZones] = useState({ shipping: [], storage: [], receiving: [] });
    const [notification, setNotification] = useState({ text: null, level: null });
    const { fbmAccount, roles } = useContext(UserContext);
    const canEditFBMItems = roles?.filter((role) => role.name.endsWith('CREATE_EDIT_FBM_ITEMS')).length >= 1;
    const showPartnerCatalog = isBlueApronOrMarleySpoonAccount(fbmAccount);

    const handleChange = (event, value) => {
        setSelectedTab(value);
    };

    useEffect(() => {
        if (!showPartnerCatalog && selectedTab === CATALOG_TAB) {
            setSelectedTab('0');
        }
    }, [selectedTab, showPartnerCatalog]);

    const getPackagingTypes = async () => {
        const response = await api.getPackagingTypes(fbmAccount.fbmAccountName);
        if (response.status === false) {
            setNotification({ text: `No packaging types found ${response.msg}`, level: 'error' });
            setPackagingTypesOpts([]);
            return;
        }

        const packagingTypeOptions = (response.data?.rows ?? []).map((row) => ({
            text: row.display_name,
            value: row.system_name,
        }));

        const pleaseSelect = { text: 'Please Select', value: 0 };
        setPackagingTypesOpts([pleaseSelect].concat(packagingTypeOptions));
    };

    const getTempZones = async () => {
        const res = await catalogApi.getItemTempZones();
        if (!res.status) {
            setNotification({ text: 'Failed to get item temp zones', level: 'error' });
            return;
        }

        setTempZones(res.data);
    };

    useEffect(() => {
        if (!canEditFBMItems) {
            return;
        }
        if (fbmAccount.id) {
            getPackagingTypes();
        }
        getTempZones();
    }, [fbmAccount, canEditFBMItems]);

    return (
        <Paper>
            <Notifications options={notification} />
            <FBMAccountPicker />
            <Tabs onChange={handleChange} value={selectedTab} indicatorColor="primary" textColor="primary" centered>
                <Tab label="View Items" value="0" />
                {canEditFBMItems && <Tab label="Create Items" value="1" />}
                <Tab label="View Packaging" value="2" />
                {canEditFBMItems && <Tab label="Create Packaging" value="3" />}
                {showPartnerCatalog && <Tab label="Catalog" value={CATALOG_TAB} />}
            </Tabs>
            <TabPanel selectedTab={selectedTab} index="0">
                <ViewFBMItems canEditFBMItems={canEditFBMItems} tempZones={tempZones} />
            </TabPanel>
            <TabPanel selectedTab={selectedTab} index="1">
                <CreateFBMItem tempZones={tempZones} />
            </TabPanel>
            <TabPanel selectedTab={selectedTab} index="2">
                <ViewFBMPackaging canEditFBMItems={canEditFBMItems} typeOpts={packagingTypesOpts} />
            </TabPanel>
            <TabPanel selectedTab={selectedTab} index="3">
                <CreateFBMPackaging typeOpts={packagingTypesOpts} />
            </TabPanel>
            {showPartnerCatalog && (
                <TabPanel selectedTab={selectedTab} index={CATALOG_TAB}>
                    <ViewPartnerCatalog />
                </TabPanel>
            )}
        </Paper>
    );
}

export { FBMCatalog };
